import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  GeneratedClientCodeLoginRequest,
  GeneratedClientLoginRequest,
  GeneratedLoginResponse,
  GeneratedUserFull,
} from '@repo/backend-api-spec';
import { EndpointError } from '@repo/components';
import { Err, ExtractErr, Ok, Result } from '@gametailors/web-core/classes';
import { AccessTokenService } from './access-token.service';
import { AuthApiService } from './api/auth-api.service';
import { UserApiService } from './api/user-api.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly user = new BehaviorSubject<GeneratedUserFull | null>(null);
  public readonly user$ = this.user.asObservable();

  private readonly authApi = inject(AuthApiService);
  private readonly userApi = inject(UserApiService);
  private readonly accessTokenService = inject(AccessTokenService);

  public async getUser(): Promise<Result<GeneratedUserFull, EndpointError>> {
    if (this.user.value) return Ok(this.user.value);

    const userMe = await this.userApi.getUserMe();
    if (userMe.isErr()) return userMe;

    this.user.next(userMe.unwrap());
    return Ok(this.user.value!);
  }

  public async logout() {
    try {
      await this.authApi.logout();
    } finally {
      // Even if the backend can't be reached, never leave the UI holding on to a session it is trying to end
      this.user.next(null);
      this.accessTokenService.clear();
    }
  }

  public async login(
    credentials: GeneratedClientLoginRequest,
  ): Promise<Result<GeneratedLoginResponse, ExtractErr<typeof this.userApi.getUserMe | typeof this.authApi.clientLogin>>> {
    const response = await this.authApi.clientLogin(credentials);
    if (response.isErr()) return Err(response.unwrapErr());

    return await this.startSession(response.unwrap());
  }

  /** Logs in with the one-time code a player gets from their therapist, instead of an email address and password. */
  public async loginWithCode(
    credentials: GeneratedClientCodeLoginRequest,
  ): Promise<Result<GeneratedLoginResponse, ExtractErr<typeof this.userApi.getUserMe | typeof this.authApi.clientCodeLogin>>> {
    const response = await this.authApi.clientCodeLogin(credentials);
    if (response.isErr()) return Err(response.unwrapErr());

    return await this.startSession(response.unwrap());
  }

  private async startSession(
    login: GeneratedLoginResponse,
  ): Promise<Result<GeneratedLoginResponse, ExtractErr<typeof this.userApi.getUserMe>>> {
    this.accessTokenService.set(login.accessToken);

    const userMe = await this.userApi.getUserMe();
    if (userMe.isErr()) return Err(userMe.unwrapErr());

    this.user.next(userMe.unwrap());

    return Ok(login);
  }

  public async isLoggedIn(): Promise<boolean> {
    try {
      const user = await this.getUser();
      // Require a CLIENT-issued token specifically: /user/me accepts a staff token too, but only a
      // patient's own client-login session should unlock this app's game.
      return user.isOk() && this.accessTokenService.isClientToken();
    } catch {
      // A failed token refresh (e.g. no refresh-token cookie at all, i.e. genuinely logged out)
      // currently throws instead of resolving to a graceful Err - see AccessTokenService.get().
      return false;
    }
  }
}
